"use client";

import { useState } from "react";
import type { ChoghadiyaResponse, ChoghadiyaPeriod } from "@/lib/api/panchang";
import type { CityData } from "@/lib/cities-data.types";
import { getQualityLabel } from "@/components/choghadiya/utils";
import { EMBED_LINK_REL } from "@/app/widgets/embed-attribution";

interface ChoghadiyaWidgetCardProps {
  data: ChoghadiyaResponse | null;
  city: CityData;
  language?: "en" | "hi";
  theme?: "light" | "dark" | "auto";
}

// Semantic status classes per Choghadiya quality. Complete literal strings so
// Tailwind's scanner sees every class; the embed never sets data-theme, so
// these single status tokens serve both widget themes.
const QUALITY_STYLES: Record<string, { chip: string; dot: string }> = {
  best: { chip: "border-success/20 bg-success/10 text-success", dot: "bg-success" },
  good: { chip: "border-success/15 bg-success/5 text-success", dot: "bg-success" },
  neutral: { chip: "border-warning/20 bg-warning/10 text-warning", dot: "bg-warning" },
  bad: { chip: "border-error/15 bg-error/5 text-error", dot: "bg-error" },
};

const QUALITY_STYLE_FALLBACK = { chip: "border-transparent", dot: "bg-transparent" };

function selectPeriods(data: ChoghadiyaResponse | null, tab: "day" | "night"): ChoghadiyaPeriod[] {
  if (!data) return [];
  return (tab === "day" ? data.day_choghadiya : data.night_choghadiya) || [];
}

function TabButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded px-2.5 py-1 font-medium transition-colors ${
        active
          ? "bg-surface-brand-strong text-text-on-brand font-semibold shadow-xs"
          : "text-text-muted dark:text-white/60 hover:text-text-primary dark:hover:text-white/90"
      }`}
    >
      {label}
    </button>
  );
}

function ChoghadiyaPeriodRow({
  period,
  isHi,
  isDark,
}: {
  period: ChoghadiyaPeriod;
  isHi: boolean;
  isDark: boolean;
}) {
  const quality = QUALITY_STYLES[period.quality] ?? QUALITY_STYLE_FALLBACK;
  return (
    <div
      className={`flex items-center justify-between rounded-lg px-2.5 py-1.5 border transition-all ${
        period.is_current
          ? "ring-1.5 ring-intent-brand bg-surface-brand-subtle dark:bg-ish-accent-light border-border-interactive dark:border-ish-accent-border"
          : isDark
            ? "bg-ish-surface border-white/10"
            : "bg-surface-canvas border-border-subtle"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full flex-shrink-0 ${quality.dot}`} />
        <span className="font-semibold text-text-primary dark:text-ish-cream">
          {isHi && period.name_hi ? period.name_hi : period.name}
        </span>
        {period.is_current && (
          <span className="rounded bg-surface-brand-strong px-1 py-0.2 text-eyebrow font-bold text-text-on-brand uppercase tracking-wider">
            {isHi ? "वर्तमान" : "Now"}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <span className={`rounded px-1.5 py-0.5 text-caption font-medium border ${quality.chip}`}>
          {getQualityLabel(period.quality)}
        </span>
        <span className="font-mono text-text-secondary dark:text-white/60 text-caption">
          {period.start_time} – {period.end_time}
        </span>
      </div>
    </div>
  );
}

export default function ChoghadiyaWidgetCard({
  data,
  city,
  language = "en",
  theme = "light",
}: ChoghadiyaWidgetCardProps) {
  const [tab, setTab] = useState<"day" | "night">("day");
  const isHi = language === "hi";
  const isDark = theme === "dark";
  const cityName = isHi && city.name_hi ? city.name_hi : city.name;

  const periods: ChoghadiyaPeriod[] = selectPeriods(data, tab);

  return (
    <div
      className={`mx-auto w-full max-w-[480px] rounded-xl border p-4 font-sans shadow-sm transition-colors ${
        isDark
          ? "border-white/10 bg-surface-dark text-ish-cream"
          : "border-border-subtle bg-surface-raised text-text-primary"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b pb-3 border-border-subtle dark:border-white/10">
        <div>
          <div className="flex items-center gap-1.5">
            <span className="text-intent-brand dark:text-ritual-gold font-serif font-bold text-base">
              ॐ
            </span>
            <h2 className="text-sm font-semibold tracking-tight">
              {isHi ? "आज का चौघड़िया" : "Today's Choghadiya"}
            </h2>
            <span className="rounded bg-surface-brand-subtle px-1.5 py-0.5 text-caption font-medium text-intent-brand dark:bg-ish-accent-light dark:text-ritual-gold">
              {cityName}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-text-muted dark:text-white/60">
            {data?.date || new Date().toISOString().split("T")[0]}
          </p>
        </div>

        {/* Day / Night Toggle */}
        <div className="flex rounded-lg border border-border-subtle dark:border-white/10 p-0.5 text-xs">
          <TabButton
            active={tab === "day"}
            label={`☀️ ${isHi ? "दिन" : "Day"}`}
            onClick={() => setTab("day")}
          />
          <TabButton
            active={tab === "night"}
            label={`🌙 ${isHi ? "रात" : "Night"}`}
            onClick={() => setTab("night")}
          />
        </div>
      </div>

      {/* Choghadiya Rows */}
      <div className="my-3 space-y-1.5 max-h-[220px] overflow-y-auto pr-1 text-xs">
        {periods.length === 0 ? (
          <p className="py-6 text-center text-text-muted">
            {isHi ? "चौघड़िया डेटा लोड हो रहा है..." : "Loading Choghadiya data..."}
          </p>
        ) : (
          periods.map((period, idx) => (
            <ChoghadiyaPeriodRow
              key={`${period.name}-${idx}`}
              period={period}
              isHi={isHi}
              isDark={isDark}
            />
          ))
        )}
      </div>

      {/* Attribution Footer */}
      <div className="flex items-center justify-between border-t pt-2.5 border-border-subtle dark:border-white/10 text-caption">
        <a
          href={`https://ishvaram.com/${isHi ? "hi/choghadiya/" : "choghadiya/"}`}
          target="_blank"
          rel={EMBED_LINK_REL}
          className="inline font-medium text-intent-brand hover:text-text-primary dark:text-ritual-gold dark:hover:text-ish-cream underline underline-offset-2 transition-colors"
        >
          {isHi ? "पूरा चौघड़िया एवं शुभ मुहूर्त →" : "Full Choghadiya & Muhurat →"}
        </a>
        <span className="text-caption text-text-muted dark:text-white/50">
          Powered by{" "}
          <a
            href="https://ishvaram.com/"
            target="_blank"
            rel={EMBED_LINK_REL}
            className="inline hover:underline font-semibold text-text-secondary dark:text-white/60"
          >
            Ishvaram
          </a>
        </span>
      </div>
    </div>
  );
}
